"use strict";

import Vue from "vue";
import axios from "axios";
import { ToastProgrammatic as Toast } from "buefy";

// Full config:  https://github.com/axios/axios#request-config
// axios.defaults.baseURL = process.env.VUE_APP_API_URL_BASE;
// axios.defaults.headers.common['Authorization'] = AUTH_TOKEN;
// axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

let config = {
  baseURL: process.env.VUE_APP_API_URL_BASE,
  transformRequest: [
    function(data) {
      // append the 'adminRouteRequest' key so that the api process the programLog only for admin actions
      // we use 1 to indicate true
      if (Vue.router.currentRoute.fullPath.includes("admin")) {
        if (data === undefined) {
          data = { adminRouteRequest: 1 };
        }
        if (data instanceof FormData) {
          data.set("adminRouteRequest", 1);
        }
        if (data instanceof Object) {
          data.adminRouteRequest = 1;
        }
      }

      return data;
    },
    ...axios.defaults.transformRequest // the last function in the array must return string and here we are using the default function
  ]
};

const parseJwt = token => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function(c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

const _axios = axios.create(config);
// add an token updating flag
let updatingToken = false;

_axios.interceptors.request.use(
  function(config) {
    let currentRouteName = Vue.router.currentRoute.name || "";
    // Do something before request is sent

    // If user is authenticated and token issued from more than 2 minutes --> refresh auth token
    if (Vue.auth.check() && !config.url.includes("refresh")) {
      const minutesPassedSinceTokenIssuedDate =
        (Date.now() / 1000 - parseJwt(Vue.auth.token()).iat) / 60;
      const refreshKey = localStorage.getItem("auth_refresh_token_default");

      if (
        minutesPassedSinceTokenIssuedDate >= 25 &&
        refreshKey !== null &&
        updatingToken === false
      ) {
        updatingToken = true;

        this.axios.get(`refresh?refreshToken=${refreshKey}`).then(() => {
          // when we receive a response means the token is updated. Didn't add a catch because invalid refresh call would lead to auto logout
          updatingToken = false;
        });
      }
    }

    const allowRequest =
      currentRouteName.includes("CreateBeWell") ||
      currentRouteName === "MemberRegistration" ||
      config.url.includes("registration") || // is used to get programId on login page
      config.url.includes("login") ||
      config.url.includes("authuser") ||
      config.url.includes("forgot-password") ||
      config.url.includes("reset-password") ||
      config.url.includes("ssoResellerLogin") ||
      config.url.includes("terryberryLogin") ||
      config.url.includes("mobile") ||
      Vue.auth.check();
    if (allowRequest) {
      return config;
    }
    if (!Vue.auth.check()) {
      // eslint-disable-next-line
      console.log("---REQUEST DENIED---");
      Vue.auth.logout();
      window.location.reload();
    }

    throw new axios.Cancel(`Request denied: ${config.url}`);
  },
  function(error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Add a response interceptor
_axios.interceptors.response.use(
  function(response) {
    // Do something with response data
    return response;
  },
  function(error) {
    // When apiv3 invalidates a token it responds with status 401 and message "Your session was ended due to inactivity"
    // we force a logout here if the token is invalidated.
    // note: no error means network error. a token can still be valid even though there might be a network error.
    // note: when our stage server is busy we see networks errors but we don't want to force a logout in that case.
    if (
      (!error.response || error.response.status === 401) &&
      error.response.data.error.message.includes("session was ended") &&
      !Vue.router.currentRoute.fullPath.includes("login")
    ) {
      if (process.env.NODE_ENV !== "production") {
        Toast.open("Login token expired. Please login to start a new session.");
      }
      Vue.auth.logout();
      window.location.reload();
    }

    if (!error.response || error.response.status === 500) {
      // We don't have to force a logout here but we let user know that there might be an issue.
      if (process.env.NODE_ENV !== "production") {
        Toast.open("Server Error. Something is not quite right. :(");
      }
    }

    // Do something with response error
    return Promise.reject(error);
  }
);

Plugin.install = function(Vue) {
  Vue.axios = _axios;
  window.axios = _axios;
  Object.defineProperties(Vue.prototype, {
    axios: {
      get() {
        return _axios;
      }
    },
    $axios: {
      get() {
        return _axios;
      }
    }
  });
};

Vue.use(Plugin);

export default Plugin;
